import React, { useState, useEffect } from 'react';
import { Calendar, Save, X } from 'lucide-react';
import { rateApi } from '../api/rateApi';
import { formatCost } from '../utils/rateFormatters';

export const RateEditor = ({ 
  rate, 
  rateIndex, 
  officeCode, 
  accessorialMap, 
  onRateUpdate,
  onRateListUpdate,
  isGlobalEditMode = false,
  onEditModeChange,
}) => {
  const [editData, setEditData] = useState({
    startDate: rate.startDate,
    endDate: rate.endDate,
    cost: rate.cost,
    rateBasis: rate.rateBasis
  });
  const [originalData, setOriginalData] = useState({});
  const [hasChanges, setHasChanges] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isNewRate, setIsNewRate] = useState(false);
  // console.log("Rendering RateEditor for rate:", rate);


  const getRateTypeColor = (rateType) => {
    const colors = {
      'BASE': 'bg-blue-100 text-blue-800 border-blue-200',
      'FSC': 'bg-green-100 text-green-800 border-green-200', 
      'DTL': 'bg-orange-100 text-orange-800 border-orange-200',
      'DTU': 'bg-purple-100 text-purple-800 border-purple-200',
      'EXM': 'bg-red-100 text-red-800 border-red-200',
      'SOC': 'bg-yellow-100 text-yellow-800 border-yellow-200',
      'UNL': 'bg-indigo-100 text-indigo-800 border-indigo-200',
      'TONU': 'bg-pink-100 text-pink-800 border-pink-200',
      'LAY': 'bg-teal-100 text-teal-800 border-teal-200',
      'default': 'bg-gray-100 text-gray-800 border-gray-200'
    };
    return colors[rateType] || colors.default;
  };

  useEffect(() => {
    const newEditData = {
      startDate: rate.startDate,
      endDate: rate.endDate,
      cost: rate.cost,
      rateBasis: rate.rateBasis
    };
    setEditData(newEditData);
    setOriginalData(newEditData);
    setHasChanges(false);
    setError(null);
    setIsNewRate(typeof rate.id === 'string' && rate.id.includes('_copy_'));
  }, [rate.startDate, rate.endDate, rate.cost, rate.rateBasis, rate.id]);

  useEffect(() => {
    if (!isGlobalEditMode) {
      const resetData = {
        startDate: rate.startDate,
        endDate: rate.endDate,
        cost: rate.cost,
        rateBasis: rate.rateBasis
      };
      setEditData(resetData);
      setOriginalData(resetData);
      setHasChanges(false);
      setError(null);
      setIsNewRate(false);
    }
  }, [isGlobalEditMode, rate.startDate, rate.endDate, rate.cost, rate.rateBasis]);

  const handleFieldChange = (field, value) => {
    const newEditData = {
      ...editData,
      [field]: value
    };
    setEditData(newEditData);
    
    const hasModifications = newEditData.startDate !== originalData.startDate || 
                            newEditData.endDate !== originalData.endDate ||
                            newEditData.cost !== originalData.cost ||
                            newEditData.rateBasis !== originalData.rateBasis;
    setHasChanges(hasModifications);
    setError(null);
  };

  const handleCreateNewRate = async () => {
    const validationError = validateData();
    if (validationError) {
      setError(validationError);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const match = rate.rateCode.match(/^(.*?)(?:-(\d+))?$/);
      const baseCode = match?.[1] || rate.rateCode;
      const currentSuffix = parseInt(match?.[2]) || 1;
      const nextSuffix = currentSuffix + 1;
      const newRateCode = `${baseCode}-${nextSuffix}`;

      const newRate = {
        ...rate,
        id: undefined, 
        rateCode: newRateCode,
        startDate: editData.startDate,
        endDate: editData.endDate,
        cost: parseFloat(editData.cost),
        rateBasis: editData.rateBasis
      };

      const response = await rateApi.createRate(officeCode, newRate);
      const createdRate = {
        ...response.data,
        isTemp: false
      };

      onRateUpdate(rateIndex, createdRate);

      setOriginalData({ ...editData });
      setHasChanges(false);
      setIsNewRate(false);

      if (onEditModeChange) {
        onEditModeChange(false);
      }

    } catch (error) {
      console.error('Error creating rate:', error);
      setError('Failed to create rate. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };


  const handleUpdateExistingRate = async () => {
    console.log("Updating existing rate");
    const validationError = validateData();
    if (validationError) {
      setError(validationError);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const updatedRate = {
        ...rate,
        startDate: editData.startDate,
        endDate: editData.endDate,
        cost: parseFloat(editData.cost),
        rateBasis: editData.rateBasis
      };

      await rateApi.updateRate(officeCode, rate.id, updatedRate);
      onRateUpdate(rateIndex, updatedRate);
      
      // Update original data to reflect saved state
      setOriginalData({ ...editData });
      setHasChanges(false);
      setIsNewRate(false);
      
      // Exit edit mode
      if (onEditModeChange) {
        onEditModeChange(false);
      }
    } catch (error) {
      console.error('Error updating rate:', error);
      setError(error.response?.data?.errorCode || 'Failed to update rate. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRevert = () => {
    // If this is a newly created rate that hasn't been saved, remove it
    if (isNewRate) {
      if (onRateListUpdate) {
        onRateListUpdate(rateIndex, rate, 'remove');
      }
    } else {
      // Revert to original data
      setEditData({ ...originalData });
      setHasChanges(false);
      setError(null);
    }
    
    if (onEditModeChange) {
      onEditModeChange(false);
    }
  };

  const validateData = () => {
    if (!editData.startDate || !editData.endDate) {
      return 'Both start and end dates are required';
    }
    
    const startDate = new Date(editData.startDate);
    const endDate = new Date(editData.endDate);
    
    if (startDate >= endDate) {
      return 'Start date must be before end date';
    }

    if (!editData.cost || isNaN(parseFloat(editData.cost))) {
      return 'Rate value must be a valid number';
    }

    if (parseFloat(editData.cost) < 0) {
      return 'Rate value cannot be negative';
    }
    
    return null;
  };

  const handleSave = () => {
    if (isNewRate) {
      handleCreateNewRate();
    } else {
      handleUpdateExistingRate();
    }
  };

  const accessorial = accessorialMap[rate.rateType] || {};
  
  return (
    <div className="space-y-3">
      {/* Error message */}
      {error && (
        <div className="p-2 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
          {error}
        </div>
      )}

      <div className="grid grid-cols-2 ml-4 mt-2 md:grid-cols-5 gap-3 text-sm">
        <div>
          <span className="text-gray-500">Charge Name</span>
          <div className="flex items-center gap-2">
            {rate.rateType && (
              <span className={`text-xs font-medium px-2 py-0.5 rounded ${getRateTypeColor(rate.rateType)}`}>
                {rate.rateType}
              </span>
            )}
            <span className="font-medium">{accessorial.accessorialName || '-'}</span>
          </div>
          {isNewRate && (
            <span className="inline-block mt-1 px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded">
              New (unsaved)
            </span>
          )}
        </div>

        <div>
          <span className="text-gray-500 flex items-center">
            <Calendar size={14} className="mr-1" />
            Start Date
          </span>
          {isGlobalEditMode ? (
            <div className="mt-1">
              <input
                type="date"
                value={editData.startDate}
                onChange={(e) => handleFieldChange('startDate', e.target.value)}
                className="block w-full px-2 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          ) : (
            <p className="font-medium">{rate.startDate}</p>
          )}
        </div>

        <div>
          <span className="text-gray-500 flex items-center">
            <Calendar size={14} className="mr-1" />
            End Date
          </span>
          {isGlobalEditMode ? (
            <div className="mt-1">
              <input
                type="date"
                value={editData.endDate}
                onChange={(e) => handleFieldChange('endDate', e.target.value)}
                className="block w-full px-2 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          ) : (
            <p className="font-medium">{rate.endDate}</p>
          )}
        </div>

        <div>
          <span className="text-gray-500">Equipment</span>
          <p className="font-medium">{rate.equipment || '-'}</p>
        </div>

        <div>
          <span className="text-gray-500">Rate Value</span>
          {isGlobalEditMode ? (
            <div className="mt-1 space-y-1">
              <div className="flex items-center space-x-2">
                <input
                  type="number"
                  step="0.1"
                  value={editData.cost}
                  onChange={(e) => handleFieldChange('cost', e.target.value)}
                  className="flex-1 px-2 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Rate value"
                />
                <span className="px-2 py-1 border border-gray-300 rounded-md text-sm">
                  {editData.currency || 'USD'}
                </span>
              </div>
              <div className="w-full">
                <span className="block w-full px-2 py-1 border border-gray-300 rounded-md text-sm">
                  {editData.rateBasis}
                </span>
              </div>
            </div>
          ) : (
            <div className="font-bold text-base text-gray-600 flex items-center">
              {formatCost(rate.cost, rate.currency, rate.rateBasis)}
            </div>
          )}
        </div>
      </div>

      {/* Save/Revert controls */}
      {isGlobalEditMode && (hasChanges || isNewRate) && (
        <div className="flex items-center justify-end space-x-2 pt-2 border-t border-gray-200">
          <button
            onClick={isNewRate ? handleSave : handleUpdateExistingRate}
            disabled={isLoading}
            className="flex items-center space-x-1 px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
          >
            <Save size={14} />
            <span>{isLoading ? 'Saving...' : (isNewRate ? 'Create Rate' : 'Save Changes')}</span>
          </button>
          <button
            onClick={handleRevert}
            disabled={isLoading}
            className="flex items-center space-x-1 px-3 py-1 bg-gray-500 text-white rounded hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
          >
            <X size={14} />
            <span>{isNewRate ? 'Cancel' : 'Revert'}</span>
          </button>
        </div>
      )}
    </div>
  );
};
